<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import SlideOver from '@/components/shared/SlideOver.vue'
import { useOrganisationStorageQuota } from '@/composables/useOrganisationStorageQuota'
import { bytesToMb, formatBytes, isValidQuotaMb, mbToBytes } from '@/lib/storageFormat'
import type { AdminStorageQuota } from '@/types/organisations'

const props = defineProps<{
  open: boolean
  organisationId: number
  // The current RAW override in bytes (null = following the platform default).
  currentOverrideBytes: number | null
  // The effective quota, used to prefill the input when there is no override.
  effectiveQuotaBytes: number
  // Counted usage, used for the "below current usage" warning.
  usedBytes: number
}>()

const emit = defineEmits<{
  close: []
  updated: [quota: AdminStorageQuota]
}>()

const { t } = useI18n()
const { quota, actionLoading, actionError, fieldErrors, setOverride } = useOrganisationStorageQuota()

const quotaMb = ref<number>(0)
const reason = ref('')
const localErrors = ref<Record<string, string>>({})

const allErrors = computed<Record<string, string>>(() => {
  const merged: Record<string, string> = { ...localErrors.value }
  for (const [key, messages] of Object.entries(fieldErrors.value)) {
    if (messages.length > 0) merged[key] = messages[0]
  }
  return merged
})

// Nothing is ever deleted: a quota below usage only blocks new uploads.
const belowUsage = computed(() => isValidQuotaMb(quotaMb.value) && mbToBytes(quotaMb.value) < props.usedBytes)

watch(
  () => props.open,
  (isOpen) => {
    if (!isOpen) return
    quotaMb.value = bytesToMb(props.currentOverrideBytes ?? props.effectiveQuotaBytes)
    reason.value = ''
    localErrors.value = {}
    fieldErrors.value = {}
    actionError.value = null
  },
  { immediate: true },
)

function handleClose(): void {
  if (actionLoading.value) return
  emit('close')
}

function validate(): boolean {
  const errs: Record<string, string> = {}
  if (!isValidQuotaMb(quotaMb.value)) {
    errs.quota_bytes = t('admin.organisations.storage_error_quota_range')
  }
  if (reason.value.trim().length < 10 || reason.value.length > 500) {
    errs.reason = t('admin.organisations.fee_error_reason_length')
  }
  localErrors.value = errs
  return Object.keys(errs).length === 0
}

async function handleSubmit(): Promise<void> {
  if (!validate()) return

  const success = await setOverride(props.organisationId, {
    quota_bytes: mbToBytes(quotaMb.value),
    reason: reason.value.trim(),
  })

  if (success && quota.value) {
    emit('updated', quota.value)
    emit('close')
  }
}
</script>

<template>
  <SlideOver
    :open="open"
    :title="t('admin.organisations.storage_edit')"
    :saving="actionLoading"
    @close="handleClose"
  >
    <div
      v-if="actionError"
      class="rounded-lg bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-300"
      role="alert"
    >
      {{ t(actionError) }}
    </div>

    <div>
      <label for="storage-quota-mb" class="block text-sm font-medium text-slate-300 mb-1">
        {{ t('admin.organisations.storage_field_quota') }}
      </label>
      <input
        id="storage-quota-mb"
        v-model.number="quotaMb"
        type="number"
        min="0"
        max="1048576"
        step="1"
        class="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
        :class="{ 'border-red-500': allErrors.quota_bytes }"
      />
      <p v-if="isValidQuotaMb(quotaMb)" class="mt-1 text-xs text-slate-500">
        {{ t('admin.organisations.storage_quota_in_bytes', { bytes: mbToBytes(quotaMb).toLocaleString('en') }) }}
      </p>
      <p v-if="allErrors.quota_bytes" class="mt-1 text-xs text-red-400" data-testid="storage-quota-error">
        {{ allErrors.quota_bytes }}
      </p>
      <p v-else class="mt-1 text-xs text-slate-500">{{ t('admin.organisations.storage_quota_help') }}</p>
    </div>

    <p
      v-if="belowUsage"
      class="rounded-lg bg-amber-500/10 border border-amber-500/30 px-4 py-3 text-sm text-amber-300"
      role="status"
      data-testid="storage-below-usage-warning"
    >
      {{ t('admin.organisations.storage_below_usage_warning', { used: formatBytes(usedBytes) }) }}
    </p>

    <div>
      <label for="storage-override-reason" class="block text-sm font-medium text-slate-300 mb-1">
        {{ t('admin.pricing.reason_label') }}
      </label>
      <textarea
        id="storage-override-reason"
        v-model="reason"
        rows="3"
        maxlength="500"
        class="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
        :class="{ 'border-red-500': allErrors.reason }"
      />
      <div class="mt-1 flex items-center justify-between">
        <p v-if="allErrors.reason" class="text-xs text-red-400">{{ allErrors.reason }}</p>
        <p v-else class="text-xs text-slate-500">{{ t('admin.pricing.reason_help') }}</p>
        <p class="text-xs text-slate-500 shrink-0 ml-2">{{ reason.length }}/500</p>
      </div>
    </div>

    <template #footer>
      <button
        type="button"
        :disabled="actionLoading"
        class="border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors disabled:opacity-50"
        @click="handleClose"
      >
        {{ t('admin.common.cancel') }}
      </button>
      <button
        type="button"
        :disabled="actionLoading"
        class="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors"
        @click="handleSubmit"
      >
        {{ t('admin.common.save') }}
      </button>
    </template>
  </SlideOver>
</template>
